import { useEffect, useMemo, useState } from "react";

type SessionStatus = "active" | "closed" | "expired" | "failed";

interface BrowserSession {
  sessionId: string;
  accessToken: string;
  status: SessionStatus;
  createdAt: string;
  lastSeenAt: string;
  liveViewUrl?: string;
  liveViewExpiresInMs?: number;
}

interface ApiSessionResponse {
  sessionId: string;
  accessToken?: string;
  status: SessionStatus;
  createdAt: string;
  lastSeenAt: string;
  liveViewUrl?: string;
  liveViewExpiresInMs?: number;
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "http://localhost:8787").replace(/\/$/, "");
const SESSION_STORAGE_KEY = "mybrowse.active-session";

function Icon({ name }: { name: "arrow" | "external" | "lock" | "refresh" | "stop" | "browser" }) {
  const paths = {
    arrow: <><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></>,
    external: <><path d="M14 5h5v5" /><path d="M19 5 10 14" /><path d="M18 13v5H5V5h5" /></>,
    lock: <><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
    refresh: <><path d="M20 11a8.1 8.1 0 0 0-14.5-4.9L4 8" /><path d="M4 4v4h4" /><path d="M4 13a8.1 8.1 0 0 0 14.5 4.9L20 16" /><path d="M20 20v-4h-4" /></>,
    stop: <><rect x="6" y="6" width="12" height="12" rx="2" /></>,
    browser: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /><path d="M7 6.5h.01M10 6.5h.01" /></>,
  } as const;

  return <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>;
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("vi-VN", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function toSession(data: ApiSessionResponse, accessToken: string): BrowserSession {
  return {
    sessionId: data.sessionId,
    accessToken,
    status: data.status,
    createdAt: data.createdAt,
    lastSeenAt: data.lastSeenAt,
    liveViewUrl: data.liveViewUrl,
    liveViewExpiresInMs: data.liveViewExpiresInMs,
  };
}

async function readApiError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string; error?: string };
    return body.message || body.error || `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}

export default function App() {
  const [destination, setDestination] = useState("https://example.com");
  const [session, setSession] = useState<BrowserSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const sessionLabel = useMemo(() => {
    if (!session) return "Chưa có phiên";
    if (session.status === "active") return "Đang hoạt động";
    if (session.status === "expired") return "Đã hết hạn";
    if (session.status === "closed") return "Đã đóng";
    return "Không khả dụng";
  }, [session]);

  useEffect(() => {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return;
    try {
      const saved = JSON.parse(raw) as BrowserSession;
      if (!saved.sessionId || !saved.accessToken) return;
      void fetch(`${API_BASE_URL}/api/sessions/${encodeURIComponent(saved.sessionId)}`, {
        headers: { Authorization: `Bearer ${saved.accessToken}` },
      }).then(async (response) => {
        if (!response.ok) {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
          return;
        }
        const data = (await response.json()) as ApiSessionResponse;
        const restored = toSession(data, saved.accessToken);
        setSession(restored);
        if (restored.status !== "active") sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }).catch(() => undefined);
    } catch {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!session || session.status !== "active") return;
    const timer = window.setInterval(() => {
      void fetch(`${API_BASE_URL}/api/sessions/${encodeURIComponent(session.sessionId)}/heartbeat`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.accessToken}` },
      }).then(async (response) => {
        if (response.ok) {
          const data = (await response.json()) as ApiSessionResponse;
          setSession((current) => current ? toSession(data, current.accessToken) : current);
        } else if (response.status === 410) {
          setSession((current) => current ? { ...current, status: "expired" } : current);
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }).catch(() => undefined);
    }, 25_000);
    return () => window.clearInterval(timer);
  }, [session?.sessionId, session?.accessToken, session?.status]);

  async function startSession() {
    setBusy(true);
    setError("");
    setMessage("");
    const popup = window.open("about:blank", "mybrowse-live-view", "popup,width=1440,height=900");
    if (!popup) {
      setBusy(false);
      setError("Trình duyệt đã chặn cửa sổ Live View. Hãy cho phép popup rồi thử lại.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ initialUrl: destination.trim() }),
      });
      if (!response.ok) throw new Error(await readApiError(response));
      const data = (await response.json()) as ApiSessionResponse;
      if (!data.accessToken || !data.liveViewUrl) throw new Error("Worker không trả về thông tin phiên hợp lệ");
      const next = toSession(data, data.accessToken);
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(next));
      setSession(next);
      popup.location.href = data.liveViewUrl;
      setMessage("Phiên đã sẵn sàng trong cửa sổ Live View mới.");
    } catch (caught) {
      popup.close();
      setError(caught instanceof Error ? caught.message : "Không thể tạo phiên browser");
    } finally {
      setBusy(false);
    }
  }

  async function openLiveView() {
    if (!session?.liveViewUrl) return renewLiveView(true);
    const popup = window.open(session.liveViewUrl, "mybrowse-live-view", "popup,width=1440,height=900");
    if (!popup) setError("Trình duyệt đã chặn popup Live View.");
  }

  async function renewLiveView(openAfter = false) {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/sessions/${encodeURIComponent(session.sessionId)}/live-view`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.accessToken}` },
      });
      if (!response.ok) throw new Error(await readApiError(response));
      const data = (await response.json()) as ApiSessionResponse;
      const next = toSession(data, session.accessToken);
      setSession(next);
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(next));
      if (openAfter && data.liveViewUrl) {
        const popup = window.open(data.liveViewUrl, "mybrowse-live-view", "popup,width=1440,height=900");
        if (!popup) setError("Trình duyệt đã chặn popup Live View.");
      }
      setMessage("Live View URL đã được làm mới.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Không thể làm mới Live View");
    } finally {
      setBusy(false);
    }
  }

  async function endSession() {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/sessions/${encodeURIComponent(session.sessionId)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session.accessToken}` },
      });
      if (!response.ok) throw new Error(await readApiError(response));
      setSession((current) => current ? { ...current, status: "closed" } : current);
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      setMessage("Phiên đã được đóng và tài nguyên remote đã được giải phóng.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Không thể kết nối Worker để đóng phiên.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="MyBrowse home">
          <span className="brand-mark"><Icon name="browser" /></span>
          <span>MyBrowse</span>
        </a>
        <div className="topbar-meta">
          <span className="secure-chip"><Icon name="lock" /> Remote workspace</span>
          <span className="availability"><span className="availability-dot" /> Browser Run</span>
        </div>
      </header>

      <main className="page-content">
        <section className="intro-section" aria-labelledby="page-title">
          <div className="eyebrow">CLOUD BROWSER / ANONYMOUS MVP</div>
          <h1 id="page-title">Một workspace browser,<br /><span>chạy trên cloud.</span></h1>
          <p className="intro-copy">Mở một phiên Chromium từ xa trên hạ tầng Cloudflare. Dữ liệu phiên chỉ dùng để vận hành và không lưu lịch sử duyệt.</p>
        </section>

        <section className="workspace-grid" aria-label="Browser workspace">
          <div className="launch-card panel">
            <div className="panel-heading">
              <div>
                <p className="section-kicker">01 / START SESSION</p>
                <h2>Khởi chạy browser remote</h2>
              </div>
              <span className="step-number">01</span>
            </div>
            <label className="field-label" htmlFor="destination">Trang bắt đầu</label>
            <div className="url-input-wrap">
              <input
                id="destination"
                value={destination}
                onChange={(event) => setDestination(event.target.value)}
                placeholder="https://example.com"
                autoComplete="url"
                spellCheck={false}
                disabled={busy || session?.status === "active"}
              />
            </div>
            <p className="field-help">Chỉ cho phép địa chỉ web công khai qua HTTPS hoặc HTTP.</p>
            <button className="primary-button" type="button" onClick={() => void startSession()} disabled={busy || session?.status === "active"}>
              <span>{busy ? "Đang khởi tạo…" : "Mở browser remote"}</span>
              <Icon name="arrow" />
            </button>
            <div className="privacy-note"><Icon name="lock" /><span>Không cần đăng nhập · Không lưu nội dung trang</span></div>
          </div>

          <div className="status-card panel">
            <div className="panel-heading">
              <div>
                <p className="section-kicker">02 / SESSION STATUS</p>
                <h2>Phiên hiện tại</h2>
              </div>
              <span className={`status-badge ${session?.status || "idle"}`}><span />{sessionLabel}</span>
            </div>
            {session ? (
              <div className="session-details">
                <div className="detail-row"><span>Session ID</span><code>{session.sessionId.slice(0, 8)}…</code></div>
                <div className="detail-row"><span>Bắt đầu lúc</span><strong>{formatTime(session.createdAt)}</strong></div>
                <div className="detail-row"><span>Heartbeat</span><strong>{session.status === "active" ? "Đang đồng bộ" : "Đã dừng"}</strong></div>
              </div>
            ) : (
              <div className="empty-state"><span className="empty-icon"><Icon name="browser" /></span><p>Chưa có browser session nào.<br />Phiên mới sẽ xuất hiện ở đây.</p></div>
            )}
            <div className="status-actions">
              <button className="secondary-button" type="button" onClick={() => void openLiveView()} disabled={!session || session.status !== "active" || busy}><Icon name="external" /> Mở Live View</button>
              <button className="icon-button" type="button" aria-label="Làm mới Live View URL" onClick={() => void renewLiveView()} disabled={!session || session.status !== "active" || busy}><Icon name="refresh" /></button>
              <button className="danger-button" type="button" onClick={() => void endSession()} disabled={!session || session.status !== "active" || busy}><Icon name="stop" /> Đóng</button>
            </div>
          </div>
        </section>

        <div className="feedback" aria-live="polite">
          {message && <span className="success-message">{message}</span>}
          {error && <span className="error-message">{error}</span>}
        </div>

        <section className="info-strip" aria-label="MyBrowse characteristics">
          <div><span className="info-index">A</span><strong>Cloud-native</strong><p>Pages + Workers + Browser Run</p></div>
          <div><span className="info-index">B</span><strong>Ephemeral by design</strong><p>Phiên tự hết hạn khi không hoạt động</p></div>
          <div><span className="info-index">C</span><strong>Server-side secrets</strong><p>Credential không vào browser bundle</p></div>
        </section>
      </main>

      <footer className="footer"><span>MYBROWSE / 2026</span><span>Built for focused remote work</span></footer>
    </div>
  );
}

